import { Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { lazy, Suspense } from "react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PageTransition } from "@/components/PageTransition";
import {
  DashboardSkeleton,
  CatalogSkeleton,
  FormSkeleton,
  DetailSkeleton,
  AuthSkeleton,
  LandingSkeleton,
  FinanceSkeleton,
} from "@/components/PageSkeletons";

// Route-specific loading skeletons
const PageLoader = ({ type = "default" }: { type?: string }) => {
  switch (type) {
    case "dashboard":
      return <DashboardSkeleton />;
    case "catalog":
      return <CatalogSkeleton />;
    case "form":
      return <FormSkeleton />;
    case "detail":
      return <DetailSkeleton />;
    case "auth":
      return <AuthSkeleton />;
    case "landing":
      return <LandingSkeleton />;
    case "finance":
      return <FinanceSkeleton />;
    default:
      return <DashboardSkeleton />;
  }
};

// Lazy load pages for better performance
// Public pages
const Index = lazy(() => import("@/pages/Index"));
const Auth = lazy(() => import("@/pages/Auth"));
const Catalogo = lazy(() => import("@/pages/Catalogo"));
const WorkDetail = lazy(() => import("@/pages/WorkDetail"));
const ComoFunciona = lazy(() => import("@/pages/ComoFunciona"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const RecuperarSenha = lazy(() => import("@/pages/RecuperarSenha"));
const RedefinirSenha = lazy(() => import("@/pages/RedefinirSenha"));
const Termos = lazy(() => import("@/pages/Termos"));
const Privacidade = lazy(() => import("@/pages/Privacidade"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Contato = lazy(() => import("@/pages/Contato"));
const AccessDenied = lazy(() => import("@/pages/AccessDenied"));
const Profile = lazy(() => import("@/pages/Profile"));
const CompletarPerfil = lazy(() => import("@/pages/CompletarPerfil"));

// Artist pages
const ArtistaDashboard = lazy(() => import("@/pages/artista/Dashboard"));
const ArtistProfile = lazy(() => import("@/pages/artista/Profile"));
const ProposalTracker = lazy(() => import("@/pages/artista/ProposalTracker"));
const ConsultationTracker = lazy(() => import("@/pages/artista/ConsultationTracker"));
const NewConsultation = lazy(() => import("@/pages/artista/NewConsultation"));
const NewProposal = lazy(() => import("@/pages/artista/NewProposal"));
const EditProposal = lazy(() => import("@/pages/artista/EditProposal"));
const EditConsultation = lazy(() => import("@/pages/artista/EditConsultation"));

// Author pages
const AutorDashboard = lazy(() => import("@/pages/autor/Dashboard"));
const AutorProposalDetail = lazy(() => import("@/pages/autor/ProposalDetail"));
const WorkFormPage = lazy(() => import("@/pages/autor/WorkFormPage"));
const AutorCaixa = lazy(() => import("@/pages/autor/Caixa"));

// Admin pages
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminUserDetail = lazy(() => import("@/pages/admin/UserDetail"));
const AdminEmailLogs = lazy(() => import("@/pages/admin/EmailLogs"));
const AdminSetup2FA = lazy(() => import("@/pages/admin/Setup2FA"));
const AdminMessages = lazy(() => import("@/pages/admin/Messages"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));

export function AnimatedRoutes() {
  const location = useLocation();

  // Determine which skeleton to show based on the current path
  const getSkeletonType = (pathname: string) => {
    if (pathname === "/") return "landing";
    if (pathname === "/auth" || pathname === "/recuperar-senha" || pathname === "/redefinir-senha") return "auth";
    if (pathname === "/catalogo") return "catalog";
    if (pathname.startsWith("/obra/")) return "detail";
    if (pathname === "/perfil" || pathname.includes("/nova") || pathname.includes("/editar")) return "form";
    if (pathname === "/autor/caixa") return "finance";
    if (pathname.startsWith("/artista") || pathname.startsWith("/autor") || pathname.startsWith("/admin")) return "dashboard";
    return "dashboard";
  };

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Suspense fallback={<PageLoader type={getSkeletonType(location.pathname)} />}>
        <Routes location={location} key={location.pathname}>
          {/* Public routes */}
          <Route
            path="/"
            element={
              <PageTransition>
                <Index />
              </PageTransition>
            }
          />
          <Route
            path="/auth"
            element={
              <PageTransition>
                <Auth />
              </PageTransition>
            }
          />
          <Route
            path="/como-funciona"
            element={
              <PageTransition>
                <ComoFunciona />
              </PageTransition>
            }
          />
          <Route
            path="/recuperar-senha"
            element={
              <PageTransition>
                <RecuperarSenha />
              </PageTransition>
            }
          />
          <Route
            path="/redefinir-senha"
            element={
              <PageTransition>
                <RedefinirSenha />
              </PageTransition>
            }
          />
          <Route
            path="/termos"
            element={
              <PageTransition>
                <Termos />
              </PageTransition>
            }
          />
          <Route
            path="/privacidade"
            element={
              <PageTransition>
                <Privacidade />
              </PageTransition>
            }
          />
          <Route
            path="/faq"
            element={
              <PageTransition>
                <FAQ />
              </PageTransition>
            }
          />
          <Route
            path="/contato"
            element={
              <PageTransition>
                <Contato />
              </PageTransition>
            }
          />
          <Route
            path="/acesso-negado"
            element={
              <PageTransition>
                <AccessDenied />
              </PageTransition>
            }
          />
          <Route
            path="/completar-perfil"
            element={
              <PageTransition>
                <CompletarPerfil />
              </PageTransition>
            }
          />

          {/* Protected catalog routes */}
          <Route
            path="/catalogo"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <Catalogo />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/obra/:id"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <WorkDetail />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <PageTransition>
                  <Profile />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Artist routes */}
          <Route
            path="/artista"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA"]}>
                <PageTransition>
                  <ArtistaDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/perfil"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA"]}>
                <PageTransition>
                  <ArtistProfile />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/proposta/:id"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA"]}>
                <PageTransition>
                  <NewProposal />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/proposta/:id/acompanhar"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA", "ADMIN"]}>
                <PageTransition>
                  <ProposalTracker />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/proposta/:id/editar"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA"]}>
                <PageTransition>
                  <EditProposal />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/consulta/:id/acompanhar"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA", "ADMIN"]}>
                <PageTransition>
                  <ConsultationTracker />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/consulta/:id/editar"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA"]}>
                <PageTransition>
                  <EditConsultation />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/consulta/nova"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA"]}>
                <PageTransition>
                  <NewConsultation />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/artista/*"
            element={
              <ProtectedRoute allowedRoles={["ARTISTA"]}>
                <PageTransition>
                  <ArtistaDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Author/Publisher routes */}
          <Route
            path="/autor"
            element={
              <ProtectedRoute allowedRoles={["AUTOR", "EDITORA"]}>
                <PageTransition>
                  <AutorDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/autor/proposta/:id"
            element={
              <ProtectedRoute allowedRoles={["AUTOR", "EDITORA"]}>
                <PageTransition>
                  <AutorProposalDetail />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/autor/obras/nova"
            element={
              <ProtectedRoute allowedRoles={["AUTOR", "EDITORA"]}>
                <PageTransition>
                  <WorkFormPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/autor/obras/:id"
            element={
              <ProtectedRoute allowedRoles={["AUTOR", "EDITORA"]}>
                <PageTransition>
                  <WorkFormPage />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/autor/caixa"
            element={
              <ProtectedRoute allowedRoles={["AUTOR", "EDITORA"]}>
                <PageTransition>
                  <AutorCaixa />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/autor/*"
            element={
              <ProtectedRoute allowedRoles={["AUTOR", "EDITORA"]}>
                <PageTransition>
                  <AutorDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Admin routes */}
          <Route
            path="/admin/setup-2fa"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <PageTransition>
                  <AdminSetup2FA />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <PageTransition>
                  <AdminDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <PageTransition>
                  <AdminUserDetail />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/emails"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <PageTransition>
                  <AdminEmailLogs />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/mensagens"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <PageTransition>
                  <AdminMessages />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notificacoes"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <PageTransition>
                  <AdminNotifications />
                </PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute allowedRoles={["ADMIN"]}>
                <PageTransition>
                  <AdminDashboard />
                </PageTransition>
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route
            path="*"
            element={
              <PageTransition>
                <NotFound />
              </PageTransition>
            }
          />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}
